import { useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useSetAtom } from 'jotai';
import useLocationMock from './useLocationMock';
import locationStateAtom from './locationStateBridge';
const useHistoryMock = () => {
  const history = useRef({});
  const location = useLocationMock();
  history.current.location = location;
  const {
    pathname
  } = location || {};
  const {
    back,
    push,
    replace
  } = useRouter();
  history.current.back = back;
  const setLocationState = useSetAtom(locationStateAtom);

  // Wrap Next.js push to support React Router v5's history.push(path, state)
  // and history.push({ pathname, search, hash, state }) signatures.
  history.current.push = useCallback((to, state) => {
    if (typeof to === 'string') {
      setLocationState(state ?? null);
      push(to);
    } else if (to && typeof to === 'object') {
      const {
        pathname: toPathname,
        search,
        hash,
        state: toState
      } = to;
      // State can come from the object or the second argument (object form
      // takes precedence per React Router v5 behavior)
      setLocationState(state ?? toState ?? null);
      const searchWithPrefix = search && !search.startsWith('?') ? `?${search}` : search ?? '';
      push(`${toPathname ?? pathname ?? ''}${searchWithPrefix}${hash ?? ''}`);
    }
  }, [pathname, push, setLocationState]);

  // Add goBack function - equivalent to window.history.back() in React Router v5
  history.current.goBack = back;

  // history.go(n) - equivalent to window.history.go(n)
  history.current.go = useCallback(n => {
    if (typeof window !== 'undefined') {
      window.history.go(n);
    }
  }, []);

  // history.action - React Router v5 tracks the last navigation action.
  // Default to 'PUSH' since Next.js doesn't expose this directly.
  // Components check `history.action === 'POP'` to detect back-navigation.
  if (!history.current.action) {
    history.current.action = 'PUSH';
  }
  const replaceMock = useCallback((newLocation, options = {}) => {
    if (!newLocation) {
      return;
    }

    // React Router v5 history.replace() never scrolls to the top of the
    // page, but Next.js router.replace() scrolls by default.  Default to
    // scroll: false to match React Router v5 semantics. Callers can still
    // opt-in via { scroll: true } when explicit scrolling is desired.
    const replaceOptions = {
      scroll: options.scroll ?? false
    };
    if (typeof newLocation === 'string') {
      replace(newLocation, replaceOptions);
    } else {
      const {
        pathname: newPathname,
        search: search_0
      } = newLocation;
      const searchWithPrefix_0 = search_0 && !search_0?.startsWith('?') ? '?' + search_0 : search_0 ?? '';
      replace(`${newPathname ?? pathname ?? ''}${searchWithPrefix_0}`, replaceOptions);
    }
  }, [pathname, replace]);
  history.current.replace = replaceMock;
  const listen = useCallback(callbackFn => {
    'use client';

    const hash_0 = window?.location?.hash;
    const handler = () => {
      if (!hash_0) {
        callbackFn();
      }
    };
    window.addEventListener('hashchange', handler);

    // Return an unlisten function for cleanup, matching React Router v5 API
    return () => {
      window.removeEventListener('hashchange', handler);
    };
  }, []);
  history.current.listen = listen;
  return history.current;
};
export default useHistoryMock;